import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { addDays, format, parseISO } from "date-fns";

import { rescheduleSchedule } from "@/api/schedule";
import { type ScheduleDetail, ScheduleType } from "@/api/schedule/models";
import { InputDropdown, InputRow, InputValue } from "@/components/common/inputs";
import { ModalAlert } from "@/components/common/modals";
import { ScheduleForm } from "@/components/features/schedule/ScheduleForm";
import { useDepartmentNavigate } from "@/hooks/common/use-department-navigate";
import { useForm } from "@/hooks/common/use-form";
import { showToast } from "@/providers";
import { RoutePathEnum } from "@/routes/paths";
import { formatDateInSaoPauloTimeZone } from "@/utils/date";
import { candidateOriginLabels } from "@/utils/features/schedule/options";

type RescheduleCandidateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  schedule: ScheduleDetail;
};

type RescheduleCandidateFormFields =
  | "scheduleType"
  | "candidateOrigin"
  | "candidateFullName"
  | "candidateBirthday"
  | "candidateEmail"
  | "candidatePhone"
  | "candidateSecondaryPhone"
  | "responsibleFullName"
  | "responsibleEmail"
  | "responsiblePhone"
  | "responsibleSecondaryPhone"
  | "scheduleDate"
  | "scheduleTime";

export const RescheduleCandidateModal = ({ isOpen, onClose, schedule }: RescheduleCandidateModalProps): JSX.Element => {
  const queryClient = useQueryClient();
  const navigate = useDepartmentNavigate();
  const today = parseISO(formatDateInSaoPauloTimeZone());
  const maximumDate = addDays(today, 6);
  const candidate = schedule.participant.candidate;
  const responsible = candidate?.responsibles[0];
  const isMinor = Boolean(candidate?.responsibles.length);
  const initialValues = {
    scheduleType: ScheduleType.NEW_CANDIDATE,
    candidateOrigin: candidate?.origin ? candidateOriginLabels[candidate.origin] : "",
    candidateFullName: schedule.participant.fullName,
    candidateBirthday: schedule.participant.birthday ? new Date(schedule.participant.birthday) : null,
    candidateEmail: candidate?.email ?? "",
    candidatePhone: candidate?.phone ?? "",
    candidateSecondaryPhone: candidate?.secondaryPhone ?? "",
    responsibleFullName: responsible?.fullName ?? "",
    responsibleEmail: responsible?.email ?? "",
    responsiblePhone: responsible?.phone ?? "",
    responsibleSecondaryPhone: responsible?.secondaryPhone ?? "",
    scheduleDate: null,
    scheduleTime: "",
  };

  const { action, control } = useForm<null, RescheduleCandidateFormFields>({
    initialValues,
    fields: {
      scheduleType: { validation: "none" },
      candidateOrigin: { validation: "none" },
      candidateFullName: { validation: "none" },
      candidateBirthday: { validation: "none" },
      candidateEmail: { validation: "emailOptional" },
      candidatePhone: { validation: "phoneOptional" },
      candidateSecondaryPhone: { validation: "phoneOptional" },
      responsibleFullName: { validation: "none" },
      responsibleEmail: { validation: "emailOptional" },
      responsiblePhone: { validation: "phoneOptional" },
      responsibleSecondaryPhone: { validation: "phoneOptional" },
      scheduleDate: { validation: "required" },
      scheduleTime: { validation: "required" },
    },
    handleSubmit: async (data) => {
      await rescheduleMutation.mutateAsync({
        scheduleDate: format(data.scheduleDate as Date, "yyyy-MM-dd"),
        scheduleTime: data.scheduleTime,
        candidate: {
          email: String(data.candidateEmail || "") || null,
          phone: String(data.candidatePhone || "") || null,
          secondaryPhone: String(data.candidateSecondaryPhone || "") || null,
          ...(isMinor
            ? {
                responsible: {
                  email: String(data.responsibleEmail || "") || null,
                  phone: String(data.responsiblePhone || "") || null,
                  secondaryPhone: String(data.responsibleSecondaryPhone || "") || null,
                },
              }
            : {}),
        },
      });
      return null;
    },
    options: { onSubmitError: () => true },
  });

  const rescheduleMutation = useMutation({
    mutationFn: ({
      scheduleDate,
      scheduleTime,
      candidate: candidatePayload,
    }: Parameters<typeof rescheduleSchedule>[1]) =>
      rescheduleSchedule(schedule.id, { scheduleDate, scheduleTime, candidate: candidatePayload }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["schedule", "agenda"], refetchType: "active" }),
        queryClient.invalidateQueries({ queryKey: ["schedule", "stage-counts"], refetchType: "active" }),
      ]);
      showToast({ type: "success", title: "Agendamento atualizado com sucesso!" });
      control.reset(initialValues);
      onClose();
      navigate(`${RoutePathEnum.SCHEDULE}?date=${variables.scheduleDate}`);
    },
    onError: (error) => {
      showToast({
        type: "error",
        title: getRescheduleErrorMessage(error),
        content: "Tente novamente em alguns instantes.",
      });
    },
  });

  const handleClose = () => {
    if (!rescheduleMutation.isPending) {
      control.reset(initialValues);
      onClose();
    }
  };

  return (
    <ModalAlert
      isOpen={isOpen}
      onClose={rescheduleMutation.isPending ? undefined : handleClose}
      className="max-w-[800px]"
      title="Reagendar candidato"
      description="Atualize os dados necessários e informe nova data e horário."
      actions={[
        ["Voltar", handleClose],
        {
          children: "Reagendar",
          type: "submit",
          form: "reschedule-candidate-form",
          isLoading: rescheduleMutation.isPending,
        },
      ]}
    >
      <form id="reschedule-candidate-form" className="form-flow" onSubmit={action}>
        <section>
          <h2 className="mb-4 text-base font-medium text-title">Tipo de agendamento</h2>
          <InputRow>
            <InputDropdown
              control={control}
              field="scheduleType"
              label="Categoria"
              options={[{ label: "Novo candidato", value: ScheduleType.NEW_CANDIDATE }]}
              disabled
            />
            <InputValue control={control} field="candidateOrigin" label="Origem" disabled />
          </InputRow>
        </section>
        <ScheduleForm
          control={control}
          mode="reschedule"
          isMinor={isMinor}
          minimumDate={today}
          maximumDate={maximumDate}
        />
      </form>
    </ModalAlert>
  );
};

function getRescheduleErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === "string") return message;
    if (Array.isArray(message)) return message.join(" ");
  }
  return "Erro ao atualizar o agendamento.";
}
